// Highlight utility functions and React hook
// Usage: import { useTextHighlight } from './highlightUtils';
import { useEffect, useState, useCallback } from 'react';

// Available highlight colors
export const HIGHLIGHT_COLORS = [
  '#FFF59D',
];

// Note highlight color
const NOTE_HIGHLIGHT_COLOR = '#FFD54F'; // distinct yellow

// Helper: create note icon
function createNoteIcon(noteText, span) {
  const icon = document.createElement('span');
  icon.textContent = '📝';
  icon.style.fontSize = '13px';
  icon.style.marginLeft = '3px';
  icon.style.cursor = 'pointer';
  icon.style.verticalAlign = 'middle';
  icon.style.display = 'none';
  icon.className = 'note-icon';
  
  icon.onclick = (e) => {
    e.stopPropagation();
    // Show note popup (read-only)
    const noteBox = document.createElement('div');
    noteBox.style.position = 'absolute';
    noteBox.style.zIndex = 10001;
    noteBox.style.background = '#fff';
    noteBox.style.border = '1px solid #aaa';
    noteBox.style.borderRadius = '6px';
    noteBox.style.padding = '8px 10px';
    noteBox.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
    noteBox.style.minWidth = '150px';
    noteBox.style.maxWidth = '300px';
    noteBox.style.wordWrap = 'break-word';
    noteBox.style.top = (icon.getBoundingClientRect().bottom + window.scrollY + 2) + 'px';
    noteBox.style.left = (icon.getBoundingClientRect().left + window.scrollX) + 'px';
    noteBox.textContent = noteText;
    
    setTimeout(() => {
      document.addEventListener('mousedown', function handler(e) {
        if (!noteBox.contains(e.target)) {
          noteBox.remove();
          document.removeEventListener('mousedown', handler);
        }
      });
    }, 10);
    
    document.body.appendChild(noteBox);
  };

  return icon;
}

// Helper: create color picker popup
function createColorPicker(colors, onSelect, onClear, onNote, onClose, position) {
  const picker = document.createElement('div');
  picker.style.position = 'absolute';
  picker.style.zIndex = 9999;
  picker.style.display = 'flex';
  picker.style.gap = '4px';
  picker.style.background = '#fff';
  picker.style.border = '1px solid #aaa';
  picker.style.borderRadius = '6px';
  picker.style.padding = '4px 6px';
  picker.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
  picker.style.top = position.top + 'px';
  picker.style.left = position.left + 'px';

  // Color buttons
  colors.forEach((color) => {
    const btn = document.createElement('button');
    btn.style.background = color;
    btn.style.width = '18px';
    btn.style.height = '18px';
    btn.style.border = '1px solid #888';
    btn.style.borderRadius = '50%';
    btn.style.cursor = 'pointer';
    btn.title = color;
    btn.onclick = (e) => {
      e.stopPropagation();
      onSelect(color);
      picker.remove();
    };
    picker.appendChild(btn);
  });

  // Note button
  const noteBtn = document.createElement('button');
  noteBtn.textContent = 'Note';
  noteBtn.style.fontSize = '12px';
  noteBtn.style.marginLeft = '4px';
  noteBtn.style.cursor = 'pointer';
  noteBtn.onclick = (e) => {
    e.stopPropagation();
    onNote();
    picker.remove();
  };
  picker.appendChild(noteBtn);

  // Change color label
  const changeLabel = document.createElement('span');
  changeLabel.textContent = '|';
  changeLabel.style.margin = '0 4px';
  picker.appendChild(changeLabel);

  // Clear button
  const clearBtn = document.createElement('button');
  clearBtn.textContent = 'Clear';
  clearBtn.style.fontSize = '12px';
  clearBtn.style.marginLeft = '2px';
  clearBtn.style.cursor = 'pointer';
  clearBtn.onclick = (e) => {
    e.stopPropagation();
    onClear();
    picker.remove();
  };
  picker.appendChild(clearBtn);

  // Close on click outside
  setTimeout(() => {
    document.addEventListener('mousedown', function handler(e) {
      if (!picker.contains(e.target)) {
        picker.remove();
        document.removeEventListener('mousedown', handler);
        if (onClose) onClose();
      }
    });
  }, 10);

  document.body.appendChild(picker);
  return picker;
}

// Helper: create note popup
function createNotePopup(onSave, onCancel, position, initialValue = '') {
  const popup = document.createElement('div');
  popup.style.position = 'absolute';
  popup.style.zIndex = 10000;
  popup.style.background = '#fff';
  popup.style.border = '1px solid #aaa';
  popup.style.borderRadius = '6px';
  popup.style.padding = '8px 10px';
  popup.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
  popup.style.top = position.top + 'px';
  popup.style.left = position.left + 'px';
  popup.style.display = 'flex';
  popup.style.flexDirection = 'column';
  popup.style.gap = '6px';

  const textarea = document.createElement('textarea');
  textarea.rows = 3;
  textarea.style.width = '180px';
  textarea.style.fontSize = '13px';
  textarea.value = initialValue;
  popup.appendChild(textarea);

  const btnRow = document.createElement('div');
  btnRow.style.display = 'flex';
  btnRow.style.justifyContent = 'flex-end';
  btnRow.style.gap = '6px';

  const saveBtn = document.createElement('button');
  saveBtn.textContent = 'Save';
  saveBtn.style.fontSize = '12px';
  saveBtn.onclick = () => { onSave(textarea.value); popup.remove(); };
  btnRow.appendChild(saveBtn);

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = 'Cancel';
  cancelBtn.style.fontSize = '12px';
  cancelBtn.onclick = () => { onCancel(); popup.remove(); };
  btnRow.appendChild(cancelBtn);

  popup.appendChild(btnRow);

  setTimeout(() => {
    document.addEventListener('mousedown', function handler(e) {
      if (!popup.contains(e.target)) {
        popup.remove();
        document.removeEventListener('mousedown', handler);
      }
    });
  }, 10);

  document.body.appendChild(popup);
  textarea.focus();
  return popup;
}

// Utility: ensure note icon exists for all highlights with note
function ensureNoteIcon(span) {
  if (!span.getAttribute('data-note')) return;
  
  // Remove existing note icon if any
  const existingIcon = span.querySelector('.note-icon');
  if (existingIcon) {
    existingIcon.remove();
  }
  
  const noteText = span.getAttribute('data-note');
  const icon = createNoteIcon(noteText, span);
  span.appendChild(icon);
  
  // Add hover effects
  span.onmouseenter = () => {
    const noteIcon = span.querySelector('.note-icon');
    if (noteIcon) noteIcon.style.display = 'inline';
  };
  span.onmouseleave = () => {
    const noteIcon = span.querySelector('.note-icon');
    if (noteIcon) noteIcon.style.display = 'none';
  };
}

// Hook to enable text highlighting in a container
export function useTextHighlight(containerRef) {
  const [activeHighlight, setActiveHighlight] = useState(null);

  // Highlight selected text with color picker and note
  const highlightSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!range || range.collapsed) return;
    if (!containerRef.current?.contains(range.commonAncestorContainer)) return;

    // Show color picker at mouse position
    const rect = range.getBoundingClientRect();
    createColorPicker(
      HIGHLIGHT_COLORS,
      (color) => {
        // No note, just highlight
        const span = document.createElement('span');
        span.style.backgroundColor = color;
        span.className = 'user-highlight';
        span.onclick = (e) => {
          e.stopPropagation();
          // Show color/clear/note popup for this span
          const spanRect = span.getBoundingClientRect();
          createColorPicker(
            HIGHLIGHT_COLORS,
            (newColor) => {
              span.style.backgroundColor = newColor;
            },
            () => {
              span.replaceWith(...span.childNodes);
              setActiveHighlight(null);
            },
            () => {
              // Add note to existing highlight
              createNotePopup(
                (noteText) => {
                  if (noteText && noteText.trim()) {
                    span.setAttribute('data-note', noteText);
                    span.style.backgroundColor = NOTE_HIGHLIGHT_COLOR;
                    ensureNoteIcon(span);
                  }
                },
                () => {},
                { top: spanRect.bottom + window.scrollY + 32, left: spanRect.left + window.scrollX },
                span.getAttribute('data-note') || ''
              );
            },
            () => {},
            { top: spanRect.bottom + window.scrollY + 2, left: spanRect.left + window.scrollX }
          );
        };
        try {
          range.surroundContents(span);
        } catch (e) {
          // fallback: do nothing
        }
        selection.removeAllRanges();
        setActiveHighlight(span);
      },
      () => {},
      // Note button handler (new highlight with note)
      () => {
        createNotePopup(
          (noteText) => {
            if (noteText && noteText.trim()) {
              const span = document.createElement('span');
              span.style.backgroundColor = NOTE_HIGHLIGHT_COLOR;
              span.className = 'user-highlight';
              span.setAttribute('data-note', noteText);
              
              span.onclick = (e) => {
                e.stopPropagation();
                // Show color/clear/note popup for this span
                const spanRect = span.getBoundingClientRect();
                createColorPicker(
                  HIGHLIGHT_COLORS,
                  (newColor) => {
                    span.style.backgroundColor = newColor;
                  },
                  () => {
                    span.replaceWith(...span.childNodes);
                    setActiveHighlight(null);
                  },
                  () => {
                    // Edit note
                    createNotePopup(
                      (newNote) => {
                        if (newNote && newNote.trim()) {
                          span.setAttribute('data-note', newNote);
                          ensureNoteIcon(span);
                        }
                      },
                      () => {},
                      { top: spanRect.bottom + window.scrollY + 32, left: spanRect.left + window.scrollX },
                      span.getAttribute('data-note') || ''
                    );
                  },
                  () => {},
                  { top: spanRect.bottom + window.scrollY + 2, left: spanRect.left + window.scrollX }
                );
              };
              
              try {
                range.surroundContents(span);
                ensureNoteIcon(span); // Add note icon immediately
              } catch (e) {
                // fallback: do nothing
              }
              selection.removeAllRanges();
              setActiveHighlight(span);
            }
          },
          () => {},
          { top: rect.bottom + window.scrollY + 32, left: rect.left + window.scrollX },
          ''
        );
      },
      () => {},
      { top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX }
    );
  }, [containerRef]);

  // Listen for mouseup to trigger highlight
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef.current) return;
      if (window.getSelection().toString()) {
        highlightSelection();
      }
    };
    const node = containerRef.current;
    if (node) node.addEventListener('mouseup', handler);
    return () => { if (node) node.removeEventListener('mouseup', handler); };
  }, [containerRef, highlightSelection]);

  // useEffect: ensure all highlights with note have icon after render
  useEffect(() => {
    if (!containerRef.current) return;
    const highlights = containerRef.current.querySelectorAll('.user-highlight');
    highlights.forEach(span => {
      if (span.getAttribute('data-note')) {
        ensureNoteIcon(span);
      }
    });
  }, [containerRef]);

  // Optional: clear all highlights
  const clearHighlights = useCallback(() => {
    if (!containerRef.current) return;
    const highlights = containerRef.current.querySelectorAll('.user-highlight');
    highlights.forEach(span => span.replaceWith(...span.childNodes));
    setActiveHighlight(null);
  }, [containerRef]);

  return { clearHighlights };
}